// src/sign_in_files/detailsScreen1.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ErrorMessage, LoadingOverlay
} from './DesignSystem';
import fundraiserApi from '../services/fundraiserApi';
import FundraiserSubmissionSuccess from '../pages/FundraiserSubmissionSuccess';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';

const DetailsScreen_1 = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // ✅ FIXED: Get userId from navigation state OR localStorage
  const userId = useMemo(() => {
    // Check navigation state first (always latest)
    if (location.state?.userId) {
      console.log('✅ DetailsScreen: Got userId from navigation state:', location.state.userId);
      return location.state.userId;
    }
    
    // Then check localStorage
    try {
      const userData = localStorage.getItem('completeUserData');
      if (userData) {
        const parsed = JSON.parse(userData);
        if (parsed.userId) {
          console.log('✅ DetailsScreen: Got userId from localStorage:', parsed.userId);
          return parsed.userId;
        }
      }
    } catch (err) {}
    
    console.warn('⚠️ DetailsScreen: No userId found');
    return null;
  }, [location.state?.userId]);
  
  // Step Management
  const [activeStep, setActiveStep] = useState(1);
  const [fundraiserId, setFundraiserId] = useState(null);
  
  // Loading States
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  
  // Success States
  const [showSubmissionSuccess, setShowSubmissionSuccess] = useState(false);
  const [generatedStory, setGeneratedStory] = useState('');
  
  // OTP States
  const [otp, setOtp] = useState('');
  const [otpTxnId, setOtpTxnId] = useState('');
  
  // UI States
  const [currentFeature, setCurrentFeature] = useState(0);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // ✅ FIXED: Redirect if no userId
  useEffect(() => {
    if (!userId) {
      console.warn('⚠️ DetailsScreen: No userId, redirecting to registration');
      navigate('/fundraiser-form');
    }
  }, [userId, navigate]);

  // Features for left section
  const features = [
    { 
      title: "Zero Platform Fees", 
      description: "100% of donations go directly to your cause", 
      gradient: "from-green-500 to-emerald-600",
      icon: "💰"
    },
    { 
      title: "24/7 Support", 
      description: "Our team is always here to help you", 
      gradient: "from-blue-500 to-cyan-600",
      icon: "🛡️"
    },
    { 
      title: "Maximum Reach", 
      description: "Share with millions of potential donors", 
      gradient: "from-purple-500 to-indigo-600",
      icon: "🌍"
    }
  ];

  // ============ EFFECTS ============
  
  // Rotate features every 4 seconds
  useEffect(() => {
    const featureInterval = setInterval(() => {
      setCurrentFeature(prev => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(featureInterval);
  }, []);

  // Load initial data
  useEffect(() => {
    if (!isInitialLoad) return;
    loadInitialData();
  }, [isInitialLoad, userId]); // ✅ Added userId dependency

  // ============ DATA LOADING ============
  
  const loadInitialData = async () => {
    // Check for successful submission
    if (checkForExistingSubmission()) {
      setIsInitialLoad(false);
      return;
    }
    
    // Load OTP data
    loadOtpData();
    
    // Load existing draft - only if we have userId
    if (userId) {
      await loadExistingDraft();
    }
    
    setIsInitialLoad(false);
  };

  const checkForExistingSubmission = () => {
    const submittedFundraiserId = localStorage.getItem('submittedFundraiserId');
    const submissionTime = localStorage.getItem('submissionTime');
    
    if (submittedFundraiserId && submissionTime) {
      const timeSinceSubmission = Date.now() - parseInt(submissionTime);
      if (timeSinceSubmission < 5 * 60 * 1000) {
        setFundraiserId(submittedFundraiserId);
        setShowSubmissionSuccess(true);
        return true;
      } else {
        localStorage.removeItem('submittedFundraiserId');
        localStorage.removeItem('submissionTime');
      }
    }
    return false;
  };

  const loadOtpData = () => {
    try {
      const userData = localStorage.getItem('completeUserData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        if (parsedData.otp && parsedData.otpTxnId) {
          setOtp(parsedData.otp);
          setOtpTxnId(parsedData.otpTxnId);
          console.log('✅ DetailsScreen: Loaded OTP from localStorage');
        }
      }
    } catch (error) {
      console.error('Error loading OTP data:', error);
    }
  };

  // ✅ FIXED: Pass userId to checkUserDraft
  const loadExistingDraft = async () => {
    if (showSubmissionSuccess || !userId) return;
    
    try {
      console.log('🔍 DetailsScreen: Checking for existing draft for userId:', userId);
      
      const draftData = await fundraiserApi.checkUserDraft(userId);
      
      if (draftData?.success && draftData.fundraiserId) {
        console.log('📄 DetailsScreen: Found existing draft:', draftData.fundraiserId);
        setFundraiserId(draftData.fundraiserId);
        localStorage.setItem('fundraiserId', draftData.fundraiserId);
        await determineCurrentStep(draftData.fundraiserId);
      }
    } catch (error) {
      console.error('Error loading draft:', error);
    }
  };

  const determineCurrentStep = async (id) => {
    try {
      const fundraiserDetails = await fundraiserApi.getFundraiserById(id);
      if (fundraiserDetails?.success) {
        const data = fundraiserDetails.data;
        if (data.story?.trim()) {
          setActiveStep(3);
        } else if (data.patientName || data.patient_name) {
          setActiveStep(2);
        } else if (data.title?.trim()) {
          setActiveStep(1);
        }
      }
    } catch (error) {
      console.error('Error determining current step:', error);
    }
  };

  // ============ HANDLERS ============
  
  const handleStep1Success = (newFundraiserId) => {
    setFundraiserId(newFundraiserId);
    localStorage.setItem('fundraiserId', newFundraiserId);
    setActiveStep(2);
  };

  const handleStep2Success = (storyContent) => {
    if (storyContent) {
      setGeneratedStory(storyContent);
      console.log('📝 DetailsScreen: Received generated story from Step2');
    }
    setActiveStep(3);
  };

  const handleStep3Success = () => {
    localStorage.setItem('submittedFundraiserId', fundraiserId);
    localStorage.setItem('submissionTime', Date.now().toString());
    setShowSubmissionSuccess(true);
  };

  const handleBack = () => {
    if (activeStep > 1) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleError = (errorMessage) => {
    setApiError(errorMessage);
  };

  const handleLoading = (loadingState) => {
    setIsLoading(loadingState);
  };

  const handleSubmitting = (submittingState) => {
    setIsSubmitting(submittingState);
  };

  // ============ RENDER HELPERS ============
  
  const getStepTitle = () => {
    switch (activeStep) {
      case 1: return 'Fundraiser Details';
      case 2: return 'Patient Information';
      case 3: return 'Tell Your Story';
      default: return '';
    }
  };

  const getStepDescription = () => {
    switch (activeStep) {
      case 1: return 'Step 1 of 3: Basic information';
      case 2: return 'Step 2 of 3: Patient details';
      case 3: return 'Step 3 of 3: Share your story';
      default: return '';
    }
  };

  const getStepIcon = () => {
    switch (activeStep) {
      case 1: return '📄';
      case 2: return '👤';
      case 3: return '📝';
      default: return '';
    }
  };

  const getLoadingMessage = () => {
    if (isSubmitting) return 'Publishing your fundraiser...';
    if (activeStep === 3) return 'Saving your story...';
    return 'Saving your fundraiser...';
  };

  // ============ RENDER ============
  
  const renderStepContent = () => {
    switch (activeStep) {
      case 1:
        return (
          <Step1
            onSuccess={handleStep1Success}
            onError={handleError}
            onLoading={handleLoading}
            onSubmitting={handleSubmitting}
            existingOtp={otp}
            existingOtpTxnId={otpTxnId}
            isLoading={isLoading}
            isSubmitting={isSubmitting}
            apiError={apiError}
            // ✅ PASS userId to Step1
            userId={userId}
          />
        );
      case 2:
        return (
          <Step2
            onSuccess={handleStep2Success}
            onError={handleError}
            onLoading={handleLoading}
            onSubmitting={handleSubmitting}
            fundraiserId={fundraiserId}
            onBack={handleBack}
            isLoading={isLoading}
            isSubmitting={isSubmitting}
            apiError={apiError}
            // ✅ PASS userId to Step2
            userId={userId}
          />
        );
      // In detailsScreen1.js - around line 200-220
case 3:
  return (
    <Step3
      onSuccess={handleStep3Success}
      onError={handleError}
      onLoading={handleLoading}
      onSubmitting={handleSubmitting}
      fundraiserId={fundraiserId}
      onBack={handleBack}
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      apiError={apiError}
      generatedStory={generatedStory}
      userId={userId}  // ✅ THIS IS MISSING - ADD IT!
    />
  );
      default:
        return null;
    }
  };

  const renderLeftSection = () => (
    <div className="hidden lg:flex lg:w-1/3 relative bg-gradient-to-br from-gsg-teal/5 via-white to-white">
      <div className="relative z-10 flex flex-col justify-center items-start w-full h-full p-12">
        <div className="max-w-lg">
          {/* Logo & Title */}
          <div className="mb-12 transition-all duration-1000">
            <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-gsg-teal to-gsg-teal-dark rounded-2xl mb-8 shadow-2xl shadow-gsg-teal/20">
              <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            
            <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Complete Your <span className="text-gsg-teal">Fundraiser</span>
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Share your story with compassionate donors worldwide.
            </p>
          </div>
          
          {/* Features */}
          <div className="transition-all duration-1000 delay-300">
            <div className="space-y-5">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className={`p-5 bg-white/95 backdrop-blur-sm rounded-2xl border-2 transition-all duration-500 
                    ${currentFeature === index 
                      ? 'border-gsg-teal/30 shadow-xl shadow-gsg-teal/10 scale-[1.02]' 
                      : 'border-gray-100 shadow-sm hover:border-gray-200'
                    }
                  `}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center flex-shrink-0 shadow-md`}>
                      <span className="text-2xl">{feature.icon}</span>
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 text-lg mb-1.5 text-left">{feature.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed text-left">{feature.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderProgressIndicator = () => (
    <div className="flex items-center justify-center space-x-12 mb-10 w-full">
      {[1, 2, 3].map((item, index) => (
        <div key={index} className="flex flex-col items-center">
          <div className={`
            w-12 h-12 rounded-full flex items-center justify-center text-base font-bold
            ${index + 1 === activeStep 
              ? 'bg-gsg-teal text-white shadow-lg' 
              : index + 1 < activeStep 
                ? 'bg-green-500 text-white' 
                : 'bg-gray-100 text-gray-500'
            }
          `}>
            {index + 1}
          </div>
          <div className="text-sm font-semibold text-gray-700 mt-3">
            {index === 0 ? 'Details' : index === 1 ? 'Patient' : 'Story'}
          </div>
          <div className="h-1 w-full bg-gray-200 mt-3 rounded-full">
            <div className={`
              h-1 w-full rounded-full
              ${index + 1 < activeStep 
                ? 'bg-green-500' 
                : index + 1 === activeStep 
                  ? 'bg-gsg-teal' 
                  : 'bg-gray-200'
              }
            `}></div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderFormHeader = () => (
    <div className="px-6 py-5 border-b border-gray-100 bg-gradient-to-r from-gsg-teal/5 to-white">
      <div className="flex items-center space-x-3">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-gsg-teal to-gsg-teal-dark flex items-center justify-center shadow-sm">
          <span className="text-lg text-white">{getStepIcon()}</span>
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">{getStepTitle()}</h3>
          <p className="text-xs text-gray-600 mt-0.5">{getStepDescription()}</p>
        </div>
      </div>
    </div>
  );

  // Debug info
  const renderDebugInfo = () => {
    if (process.env.NODE_ENV === 'production') return null;
    
    return (
      <div className="mb-4 p-2 bg-gray-100 border border-gray-200 rounded text-xs">
        <details>
          <summary className="cursor-pointer font-medium">Debug Info</summary>
          <div className="mt-2 space-y-1">
            <p><span className="font-medium">User ID:</span> {userId || '❌ Not found'}</p>
            <p><span className="font-medium">Fundraiser ID:</span> {fundraiserId || 'Not set'}</p>
            <p><span className="font-medium">Active Step:</span> {activeStep}</p>
            <p><span className="font-medium">State:</span> {JSON.stringify(location.state, null, 2)}</p>
          </div>
        </details>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gsg-teal/5">
      {/* Loading Overlay */}
      {(isLoading || isSubmitting) && (
        <LoadingOverlay message={getLoadingMessage()} />
      )}
      
      {/* Debug Info */}
      {renderDebugInfo()}
      
      <div className="relative min-h-screen flex">
        {/* Left Section */}
        {renderLeftSection()}

        {/* Center Content */}
        <div className="w-full lg:w-2/3 flex flex-col justify-center items-center py-6 px-4 lg:px-8">
          <div className="w-full max-w-3xl mx-auto">
            {/* Centered Heading */}
            <div className="text-center mb-10 w-full">
              <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                Complete Your Fundraiser
              </h2>
              <p className="text-gray-600 mt-3 text-lg">
                Share your story with compassionate donors worldwide
              </p>
            </div>
            
            {/* Progress Indicator */}
            {renderProgressIndicator()}

            {/* Form Container */}
            <div className="w-full flex justify-center">
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 w-full max-w-2xl">
                {/* Form Header */}
                {renderFormHeader()}
                
                {/* Form Content */}
                <div className="px-6 py-5">
                  {apiError && <ErrorMessage message={apiError} type="error" />}
                  {renderStepContent()}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="text-center mt-8 w-full">
              <p className="text-xs text-gray-500">
                By continuing, you agree to our Terms & Privacy Policy
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Success Screen */}
      {showSubmissionSuccess && (
        <FundraiserSubmissionSuccess 
          fundraiserId={fundraiserId}
          onClose={() => {
            setShowSubmissionSuccess(false);
            localStorage.removeItem('submittedFundraiserId');
            localStorage.removeItem('submissionTime');
            navigate('/dashboard');
          }}
        />
      )}
    </div>
  );
};

export default DetailsScreen_1;